(() => {
  const SECTION_SELECTOR = '.oddit-header';
  const abortControllers = new Map();

  function initSection(section) {
    const sectionId = section.closest('[id^="shopify-section-"]')?.id || section.id;

    // Abort any previous listeners for this section
    if (abortControllers.has(sectionId)) {
      abortControllers.get(sectionId).abort();
    }

    const controller = new AbortController();
    const signal = controller.signal;
    abortControllers.set(sectionId, controller);

    const menuToggle = section.querySelector('[data-header-menu-toggle]');
    const mobileNav = section.querySelector('[data-mobile-nav]');
    const mobileNavOverlay = section.querySelector('[data-mobile-nav-overlay]');
    const megaMenuTriggers = section.querySelectorAll('[data-mega-menu-trigger]');
    const megaMenuPanels = section.querySelectorAll('[data-mega-menu-panel]');
    const cartToggles = section.querySelectorAll('[data-cart-toggle]');
    const megaMenuOverlay = section.querySelector('[data-mega-menu-overlay]');

    // Move the overlay out of the header into <body> so it can sit
    // above page content (z-index 999) but below the header (z-index 1000).
    // Inside the header's stacking context it would be trapped above or
    // below the header content with no way to cover the page.
    if (megaMenuOverlay && megaMenuOverlay.parentNode !== document.body) {
      document.body.appendChild(megaMenuOverlay);
    }

    // Track header's bottom edge in viewport coords so the mobile nav can
    // sit exactly in the visible space below it (used by `top: var(...)`).
    function updateHeaderBottom() {
      const rect = section.getBoundingClientRect();
      const offset = Math.max(0, rect.bottom);
      document.documentElement.style.setProperty('--oddit-header-bottom', (offset - 12) + 'px');
    }
    updateHeaderBottom();
    window.addEventListener('resize', updateHeaderBottom, { signal });
    window.addEventListener('scroll', updateHeaderBottom, { signal, passive: true });

    // --- Mobile nav ---
    function openMobileNav() {
      if (!mobileNav) return;
      mobileNav.classList.add('is-open');
      mobileNav.setAttribute('aria-hidden', 'false');
      document.body.style.overflow = 'hidden';
      // Reveal all interaction elements immediately rather than waiting for
      // them to scroll into view inside the panel.
      mobileNav.querySelectorAll('.oddit-reveal, .oddit-entrance').forEach(function (el) {
        el.classList.add('is-visible');
      });
    }

    function closeMobileNav() {
      if (!mobileNav) return;
      mobileNav.classList.remove('is-open');
      mobileNav.setAttribute('aria-hidden', 'true');
      document.body.style.overflow = '';
    }

    if (menuToggle) {
      menuToggle.addEventListener('click', function () {
        if (mobileNav && mobileNav.classList.contains('is-open')) {
          closeMobileNav();
        } else {
          openMobileNav();
        }
      }, { signal });
    }

    if (mobileNavOverlay) {
      mobileNavOverlay.addEventListener('click', closeMobileNav, { signal });
    }

    // --- Mega menu ---
    let megaMenuCloseTimer = null;

    function closeAllMegaMenus() {
      megaMenuPanels.forEach(function (panel) {
        panel.classList.remove('is-open');
        panel.setAttribute('aria-hidden', 'true');
      });
      megaMenuTriggers.forEach(function (trigger) {
        trigger.setAttribute('aria-expanded', 'false');
      });
      if (megaMenuOverlay) megaMenuOverlay.classList.remove('is-active');
    }

    function openMegaMenu(trigger) {
      clearTimeout(megaMenuCloseTimer);
      const isCurrentlyOpen = trigger.getAttribute('aria-expanded') === 'true';
      if (isCurrentlyOpen) return;

      closeAllMegaMenus();

      const label = trigger.getAttribute('data-mega-menu-label');
      const targetPanel = section.querySelector(
        '[data-mega-menu-panel][data-mega-menu-label="' + label + '"]'
      );

      if (targetPanel) {
        if (megaMenuOverlay) megaMenuOverlay.classList.add('is-active');
        targetPanel.classList.add('is-open');
        targetPanel.setAttribute('aria-hidden', 'false');
        trigger.setAttribute('aria-expanded', 'true');
      }
    }

    function toggleMegaMenu(trigger) {
      const isCurrentlyOpen = trigger.getAttribute('aria-expanded') === 'true';
      if (isCurrentlyOpen) {
        closeAllMegaMenus();
      } else {
        openMegaMenu(trigger);
      }
    }

    function scheduleMegaMenuClose() {
      clearTimeout(megaMenuCloseTimer);
      megaMenuCloseTimer = setTimeout(closeAllMegaMenus, 250);
    }

    function cancelMegaMenuClose() {
      clearTimeout(megaMenuCloseTimer);
    }

    megaMenuTriggers.forEach(function (trigger) {
      trigger.addEventListener('click', function (e) {
        e.preventDefault();
        toggleMegaMenu(trigger);
      }, { signal });

      trigger.addEventListener('mouseenter', function () {
        cancelMegaMenuClose();
        openMegaMenu(trigger);
      }, { signal });
    });

    // Cursor tracking across the whole header. Stay open while over a
    // trigger or panel; schedule close anywhere else (with a short grace
    // period so the cursor can transit the padding between them).
    section.addEventListener('mouseover', function (e) {
      const isOverTrigger = !!e.target.closest('[data-mega-menu-trigger]');
      const isOverPanel = !!e.target.closest('.oddit-header__mega-menu');
      if (isOverTrigger || isOverPanel) {
        cancelMegaMenuClose();
      } else {
        scheduleMegaMenuClose();
      }
    }, { signal });

    // Close immediately when the cursor leaves the header entirely.
    section.addEventListener('mouseleave', closeAllMegaMenus, { signal });

    // Close mega menu when clicking outside
    document.addEventListener('click', function (e) {
      if (!e.target.closest('.oddit-header__mega-menu') &&
          !e.target.closest('[data-mega-menu-trigger]')) {
        closeAllMegaMenus();
      }
    }, { signal });

    // Close mega menu on Escape key
    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape') {
        closeAllMegaMenus();
        closeMobileNav();
      }
    }, { signal });

    // --- Cart toggle ---
    cartToggles.forEach(function (toggle) {
      toggle.addEventListener('click', function () {
        if (window.OdditCartDrawer) {
          window.OdditCartDrawer.open();
        }
      }, { signal });
    });

    // --- Cart count badge (listen for cart updates) ---
    document.addEventListener('cart:updated', function (e) {
      const cart = e.detail.cart;
      const badges = document.querySelectorAll('[data-cart-count]');
      badges.forEach(function (badge) {
        badge.textContent = cart.item_count;
        badge.style.display = cart.item_count > 0 ? '' : 'none';
      });
    }, { signal });
  }

  // Init on DOM ready
  document.querySelectorAll(SECTION_SELECTOR).forEach(initSection);

  // Shopify theme editor events
  document.addEventListener('shopify:section:load', function (e) {
    const section = e.target.querySelector(SECTION_SELECTOR);
    if (section) initSection(section);
  });

  document.addEventListener('shopify:section:unload', function (e) {
    const sectionId = e.target.id;
    if (abortControllers.has(sectionId)) {
      abortControllers.get(sectionId).abort();
      abortControllers.delete(sectionId);
    }
  });
})();